import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import RadioGroup from "@mui/material/RadioGroup";
import { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
  CompaniesDto,
  ROUTES,
  useMediaQueries
} from "../../../../../../../../shared";
import RadioListItem from "../../../../../../../RadioListItem/RadioListItem";
import { useStyles } from "../../../../../../styles";

interface ICompanyListProps {
  route: ROUTES;
  searchValue: string;
  companies: CompaniesDto[];
  selectedCompany: string | CompaniesDto;
  handleChangeSelection: (value: string) => Promise<void>;
}

const CompanyList = ({
  route,
  searchValue,
  companies,
  selectedCompany,
  handleChangeSelection
}: ICompanyListProps) => {
  const { fromMd } = useMediaQueries();
  const { t } = useTranslation();

  const {
    childCompaniesCount,
    selectCompanyRadioGroupCSS,
    noCompaniesFoundCSS
  } = useStyles(fromMd);

  const normalizeSearchString = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const findScrollElement = (startElement: HTMLElement | null) => {
    let current: HTMLElement | null = startElement;

    while (current) {
      const { overflowY } = window.getComputedStyle(current);
      if (overflowY === "auto" || overflowY === "scroll") return current;
      current = current.parentElement;
    }

    return null;
  };

  const filteredCompanyListBySearchValue = useMemo(() => {
    const normalizedSearchValue = normalizeSearchString(searchValue);

    return companies.filter((company: CompaniesDto) =>
      normalizeSearchString(company.name).includes(normalizedSearchValue)
    );
  }, [companies, searchValue]);

  const radioGroupRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const resolvedFor = useRef<{
    radioGroup: HTMLDivElement;
    fromMd: boolean;
  } | null>(null);

  const [scrollElement, setScrollElement] = useState<
    HTMLElement | null | undefined
  >(undefined);

  const [itemsOffset, setItemsOffset] = useState(0);

  useLayoutEffect(() => {
    const radioGroup = radioGroupRef.current;

    if (!radioGroup) return;
    if (
      resolvedFor.current?.radioGroup !== radioGroup ||
      resolvedFor.current?.fromMd !== fromMd
    ) {
      resolvedFor.current = { radioGroup, fromMd };
      setScrollElement(
        fromMd ? radioGroup : findScrollElement(radioGroup.parentElement)
      );
      return;
    }

    if (scrollElement && itemsRef.current) {
      const offset = Math.round(
        itemsRef.current.getBoundingClientRect().top -
          scrollElement.getBoundingClientRect().top +
          scrollElement.scrollTop -
          scrollElement.clientTop
      );

      setItemsOffset(current => (current === offset ? current : offset));
    }
  });

  const virtualizer = useVirtualizer({
    count: filteredCompanyListBySearchValue.length,
    getScrollElement: () => scrollElement ?? null,
    estimateSize: () => 48,
    overscan: 10,
    gap: 6,
    scrollMargin: itemsOffset,
    enabled: Boolean(scrollElement),
    getItemKey: itemIndex =>
      filteredCompanyListBySearchValue[itemIndex]?.id ?? itemIndex
  });

  const virtualItems = scrollElement ? virtualizer.getVirtualItems() : [];

  const paddingTop = virtualItems.length
    ? virtualItems[0].start - itemsOffset
    : 0;

  const paddingBottom = virtualItems.length
    ? virtualizer.getTotalSize() -
      (virtualItems[virtualItems.length - 1].end - itemsOffset)
    : 0;

  const renderItems = () => {
    if (!scrollElement) return null;

    return virtualItems.map((virtualItem, position) => {
      const company = filteredCompanyListBySearchValue[virtualItem.index];

      return (
        <div
          key={virtualItem.key}
          ref={virtualizer.measureElement}
          data-index={virtualItem.index}
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: position === 0 ? 0 : "6px"
          }}
        >
          <RadioListItem
            data-testid="company-list-item"
            route={route}
            value={company.id}
            label={company.name}
            selectedValue={selectedCompany}
          />
        </div>
      );
    });
  };

  return (
    <>
      {searchValue.length > 0 &&
      filteredCompanyListBySearchValue.length === 0 ? (
        <Typography
          variant="body2Medium"
          component="div"
          sx={noCompaniesFoundCSS as SxProps<Theme>}
        >
          {t("Table##empty table##no results found##title")}
        </Typography>
      ) : (
        <RadioGroup
          ref={radioGroupRef}
          id="select-company-radio-group"
          data-testid="select-company-radio-group"
          value={selectedCompany}
          onChange={(_event, value) => handleChangeSelection(value)}
          sx={selectCompanyRadioGroupCSS as SxProps<Theme>}
        >
          <Typography
            variant="body3SemiBold"
            sx={childCompaniesCount as SxProps<Theme>}
          >
            {t("Selector##companies", {
              companyCount: companies.length
            })}
          </Typography>

          <div ref={itemsRef} style={{ paddingTop, paddingBottom }}>
            {renderItems()}
          </div>
        </RadioGroup>
      )}
    </>
  );
};

export default CompanyList;
